import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'app-icon-button',
  templateUrl: './icon-button.component.html',
  styleUrls: ['./icon-button.component.scss'],
  imports: [MatIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IconButtonComponent {
  /** The name of the material icon to display (optional) */
  iconName = input<string | undefined>(undefined);

  /** The shape of the button ('round' | 'square') (optional, default: 'round') */
  buttonShape = input<'round' | 'square'>('round');

  /** The visual style of the button */
  buttonStyle = input<
    | 'transparent--blue'
    | 'transparent--dark-blue'
    | 'transparent'
    | 'primary'
    | 'secondary'
    | 'pause'
    | 'outlined'
    | 'positive'
    | 'negative'
    | 'grey'
  >('primary');

  /** Text color variant (optional, default: 'standard') */
  textColor = input<'red' | 'standard'>('standard');

  /** The label text (optional) */
  label = input<string | undefined>(undefined);

  /** CSS class for label size styling */
  labelClass = input<'large' | ''>('');

  /** The HTML button type attribute (optional, default: 'button') */
  buttonType = input<'button' | 'submit' | 'reset'>('button');

  /** The icon position ('left' | 'right') (optional, default: 'left') */
  iconPosition = input<'left' | 'right'>('left');

  /** ID for the button element (optional) */
  buttonId = input<string | undefined>(undefined);

  /** Whether the button is disabled (optional, default: false) */
  isDisabled = input<boolean>(false);

  /** Whether the button is in a processing/loading state (optional, default: false) */
  isProcessing = input<boolean>(false);

  /** Event emitted when the button is clicked */
  buttonClicked = output<void>();

  /* computed classes */
  public classes = computed(() => {
    const disabled = this.isDisabled() ? 'disabled' : '';
    const hasText = this.label() ? 'icon-button--text' : '';
    const textColor = this.textColor()
      ? `icon-button--text--${this.textColor()}`
      : '';
    const labelClass =
      this.labelClass() === 'large' ? 'button-large' : 'button-text';

    return [
      'icon-button',
      this.buttonShape(),
      'button-text',
      `icon-button--${this.labelClass()}`,
      `icon-button--${this.buttonStyle()}`,
      textColor,
      hasText,
      labelClass,
      disabled,
    ]
      .filter(Boolean)
      .join(' ');
  });

  public iconClasses = computed(() => (this.isProcessing() ? 'spin' : ''));

  clickButton = (): void => {
    this.buttonClicked.emit();
  };
}
